import type {
  BodyProps,
  CenterProps,
  ComponentIconProp,
  IconProps,
  IntrinsicSystemProps,
} from '@hover/blueprint';
import {
  Body,
  Center,
  Icon,
  Pictogram,
  VStack,
  forwardRef,
  system,
  transition,
} from '@hover/blueprint';
import type { IconProp, PictogramProp } from '@hover/icons';
import type { ReactNode } from 'react';
import React from 'react';

import { Skeleton } from 'src/shared/components/Skeleton/Skeleton';

interface TileProps extends CenterProps {
  colorScheme?: 'light' | 'dark';
}

const Tile = ({ colorScheme = 'dark', sx, ...props }: TileProps) => {
  const baseColor = colorScheme === 'dark' ? 'neutral.800' : 'neutral.50';
  const hoverColor = colorScheme === 'dark' ? 'neutral.700' : 'neutral.100';
  const activeColor = colorScheme === 'dark' ? 'neutral.600' : 'neutral.200';
  return (
    <Center
      _groupActive={{ bg: `${activeColor} !important` }}
      _groupDisabled={{
        color: activeColor,
        cursor: 'default',
        bg: `${baseColor} !important`,
      }}
      _groupHover={{ bg: hoverColor }}
      as="div"
      bg={baseColor}
      borderRadius="300"
      color="inherit"
      sx={{ ...sx, aspectRatio: '1' }}
      {...transition.easing250('background-color', 'color')}
      {...props}
    />
  );
};

interface CenterButtonProps extends IntrinsicSystemProps<'button'> {}

const CenterButton = forwardRef<CenterButtonProps, 'button'>((props, ref) => (
  <system.button
    alignItems="center"
    bg="transparent"
    border="none"
    color="neutral.0"
    cursor="pointer"
    data-group
    display="flex"
    flexDirection="column"
    justifyContent="center"
    p={0}
    {...props}
    ref={ref}
  />
));

export interface TileIconButtonProps extends CenterButtonProps {
  label: ReactNode;
  icon: ComponentIconProp;
}

/**
 * A button that renders a system icon in a "tile" with a label _within_ the
 * tile
 *
 * @todo(design-system): extract this as the button for the "Action Bar"
 * component
 * @see {@link https://app.asana.com/0/1204161744428587/1206595105287973}
 */
export const TileIconButton = forwardRef<TileIconButtonProps, 'button'>(
  ({ label, icon, ...props }, ref) => (
    <CenterButton {...props} ref={ref}>
      <Tile flexDirection="column" gap="100" width="100%">
        <Icon icon={icon} size="small" />
        <Body color="inherit" isSemiBold m={0} size={300}>
          {label}
        </Body>
      </Tile>
    </CenterButton>
  ),
);

export interface TileButtonProps extends CenterButtonProps {
  label: ReactNode;
  icon: ComponentIconProp;
  isLoading?: boolean;
  colorScheme?: 'light' | 'dark';
  textSize?: BodyProps['size'];
  iconSize?: IconProps['height'];
}

/**
 * A button that renders a system icon or custom icon in a "tile" with a label
 * _below_ the tile
 *
 * @todo(design-system): extract this as the button for the "Action Bar"
 * component
 * @see {@link https://app.asana.com/0/1204161744428587/1206595105287973}
 */
export const TileButton = forwardRef<TileButtonProps, 'button'>(
  (
    { label, icon, isLoading, colorScheme, textSize, iconSize = 300, ...props },
    ref,
  ) => (
    <CenterButton gap="200" {...props} ref={ref}>
      <Skeleton colorScheme="light" isLoaded={!isLoading} width="100%">
        <Tile colorScheme={colorScheme}>
          <system.svg
            as={icon}
            color={colorScheme === 'dark' ? 'neutral.0' : 'neutral.900'}
            height={iconSize}
            width={iconSize}
          />
        </Tile>
      </Skeleton>
      <Skeleton colorScheme="light" isLoaded={!isLoading}>
        <Body
          color="inherit"
          isSemiBold
          m={0}
          size={textSize}
          textColor={colorScheme === 'light' ? 'black' : 'white'}
        >
          {label}
        </Body>
      </Skeleton>
    </CenterButton>
  ),
);

export interface TileButtonPictogramProps extends CenterButtonProps {
  label: ReactNode;
  icon: IconProp;
  pictogram?: PictogramProp;
  isLoading?: boolean;
  colorScheme?: 'light' | 'dark';
  textSize?: BodyProps['size'];
  iconSize?: IconProps['height'];
  isActive?: boolean;
}

export const TileButtonPictogram = forwardRef<
  TileButtonPictogramProps,
  'button'
>(
  (
    {
      label,
      icon,
      pictogram,
      isLoading,
      colorScheme,
      textSize,
      isActive,
      ...props
    },
    ref,
  ) => {
    const color = colorScheme === 'dark' ? 'neutral.0' : 'neutral.900';

    return (
      <CenterButton gap="200" {...props} ref={ref}>
        <Skeleton colorScheme="light" isLoaded={!isLoading} width="100%">
          <Tile colorScheme={colorScheme}>
            <VStack>
              <Center />
              <Center>
                <Icon
                  color={color}
                  display={{
                    base: 'flex',
                    tablet: pictogram ? 'none' : 'flex',
                  }}
                  height={200}
                  icon={icon}
                  width={200}
                />
                {pictogram && (
                  <Pictogram
                    color={color}
                    display={{ base: 'none', tablet: 'flex' }}
                    pictogram={pictogram}
                  />
                )}
              </Center>
              <Center
                bg={color}
                borderRadius="50%"
                height="4px"
                id="scott"
                visibility={isActive ? 'visible' : 'hidden'}
                width="4px"
              />
            </VStack>
          </Tile>
        </Skeleton>
        <Skeleton colorScheme="light" isLoaded={!isLoading}>
          <Body
            color="inherit"
            isSemiBold
            m={0}
            size={textSize}
            textColor={colorScheme === 'light' ? 'black' : 'white'}
          >
            {label}
          </Body>
        </Skeleton>
      </CenterButton>
    );
  },
);
